
import Vue from 'vue'
import { getInstance as getAuthInstance } from '@/auth/authWrapper'
import { getPromoByKey, resolvePromo } from '@/api/promo'
import type { PromoResolvePayload } from '@/api/promo'
import PromoProjectSelect from '@/components/promo/PromoProjectSelect.vue'
import {
    selectPromoKey,
    withChatFullscreenQuery,
    withOpenSmsPromoQuery,
    withProjectPrefix,
} from '@/helpers/promo/session'
import {
    claimableSmsKeysFromResolve,
    pickPromoRedirectByToggle,
    resolveNeedsProjectSelector,
} from '@/helpers/promo/parsePromoBundle'
import { setRedirectAfterOnboardingPath } from '@/helpers/redirectAfterOnboarding'
import { sendGoal } from '@/helpers/ymLogger'

type StoreProject = { id?: string; [key: string]: unknown }

export default Vue.extend({
    name: 'PromoResolve',
    components: { PromoProjectSelect },
    data: () => ({
        statusText: 'Проверяем промо…',
        handled: false,
        runInFlight: false,
        waitingForAuth: false,
        showSelector: false,
        previewPayload: null as PromoResolvePayload | null,
        unwatchAuthAuthenticated: null as (() => void) | null,
        unwatchAuthLoading: null as (() => void) | null,
    }),
    computed: {
        promoKey(): string {
            return selectPromoKey(this.$route.params.key)
        },
        heading(): string {
            return this.waitingForAuth
                ? 'Для активации промо нужна авторизация'
                : this.statusText
        },
    },
    watch: {
        '$route.params.key'() {
            this.handled = false
            this.waitingForAuth = false
            this.showSelector = false
            this.previewPayload = null
            this.run()
        },
    },
    mounted() {
        const auth = getAuthInstance()
        const resumeAfterAuth = () => {
            if (!this.waitingForAuth || auth.loading || !auth.isAuthenticated) return
            this.waitingForAuth = false
            this.logPromo('auth_completed_after_wait')
            this.run()
        }
        this.unwatchAuthAuthenticated = auth.$watch('isAuthenticated', resumeAfterAuth)
        this.unwatchAuthLoading = auth.$watch('loading', resumeAfterAuth)
        this.run()
    },
    beforeDestroy() {
        this.unwatchAuthAuthenticated?.()
        this.unwatchAuthAuthenticated = null
        this.unwatchAuthLoading?.()
        this.unwatchAuthLoading = null
    },
    methods: {
        logPromo(stage: string, details: Record<string, unknown> = {}) {
            console.info('[promo-flow]', {
                stage,
                promoKey: this.promoKey || null,
                routePath: this.$route.path,
                ...details,
            })
        },
        async waitAuthReady(): Promise<boolean> {
            const auth = getAuthInstance()
            if (!auth.loading) {
                const authenticated = Boolean(auth.isAuthenticated)
                this.logPromo('auth_ready', { authenticated, waited: false })
                return authenticated
            }
            this.logPromo('auth_wait_started')
            let unwatch: (() => void) | undefined
            await new Promise<void>((resolve) => {
                unwatch = auth.$watch(
                    'loading',
                    (loading: boolean) => {
                        if (!loading) resolve()
                    },
                    { immediate: true }
                )
            })
            unwatch?.()
            const authenticated = Boolean(auth.isAuthenticated)
            this.logPromo('auth_ready', { authenticated, waited: true })
            return authenticated
        },
        openAuthDialog() {
            this.$store.commit('setAuthDialogStartSection', 'login')
            this.$store.commit('setIsAuthDialog', true)
        },
        goCreateProject() {
            const path = `/promo/${this.promoKey}`
            setRedirectAfterOnboardingPath(path)
            this.$router
                .replace({
                    name: 'OnBoardingCreateProject',
                    query: { redirectAfterOnboarding: path },
                })
                .catch(() => undefined)
        },
        normalizeRedirectPath(path: string | null | undefined): string | null {
            if (!path || typeof path !== 'string') return null
            const trimmed = path.trim()
            if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('://')) {
                return null
            }
            return trimmed
        },
        goToRedirect(path: string) {
            const redirectPath = withChatFullscreenQuery(path)
            this.statusText = 'Переходим…'
            this.logPromo('redirect_started', { redirectPath })
            this.$router.replace(redirectPath).catch(() => undefined)
        },
        storeProjects(): StoreProject[] {
            return (this.$store.state.projects as StoreProject[]) || []
        },
        findStoreProject(projectId: string): StoreProject | null {
            const id = String(projectId || '').trim()
            if (!id) return null
            return this.storeProjects().find((project) => String(project?.id || '').trim() === id) || null
        },
        async ensureProjectsLoaded() {
            if (this.$store.state.project?.areAllProjectsLoaded) return
            await this.$store.dispatch('project/setProjects', { silent: true })
        },
        async openProjectSelector() {
            try {
                await this.ensureProjectsLoaded()
            } catch (error) {
                this.logPromo('projects_load_failed', {
                    error: error instanceof Error ? error.message : String(error),
                })
                this.$store.dispatch('callNotify', 'Не удалось загрузить список проектов')
                this.$router.replace('/projects').catch(() => undefined)
                return
            }
            const projects = this.storeProjects().filter((project) => String(project?.id || '').trim())
            if (projects.length === 0) {
                this.logPromo('project_creation_required', { reason: 'selector_empty_list' })
                this.statusText = 'Создайте проект…'
                this.goCreateProject()
                return
            }
            if (projects.length === 1) {
                await this.resolveAndGo(String(projects[0].id))
                return
            }
            this.showSelector = true
            this.logPromo('project_selector_opened', { projectsCount: projects.length })
            // Цель Метрики: открылась страница выбора проекта (промо-флоу)
            void sendGoal('promo_project_select_open', { projectsCount: projects.length })
        },
        async onProjectSelected(projectId: string) {
            if (this.runInFlight) return
            this.showSelector = false
            this.runInFlight = true
            try {
                await this.resolveAndGo(projectId)
            } finally {
                this.runInFlight = false
            }
        },
        async resolveAndGo(projectId: string) {
            this.statusText = 'Загружаем промо…'
            this.logPromo('resolve_started', { projectId })
            const { data, error } = await resolvePromo(this.promoKey, projectId)
            if (error || !data) {
                this.logPromo('resolve_failed', {
                    error: typeof error === 'string' ? error : String(error ?? 'empty_response'),
                    hasData: Boolean(data),
                    projectId,
                })
                this.statusText = 'Не удалось проверить промо'
                this.$store.dispatch(
                    'callNotify',
                    typeof error === 'string' ? error : 'Не удалось проверить промо'
                )
                this.$router.replace('/projects').catch(() => undefined)
                return
            }

            this.logPromo('resolve_completed', {
                status: data.status,
                projectId: data.projectId,
                redirectPath: data.redirectPath,
            })

            if (data.status === 'not_found') {
                this.logPromo('run_failed', { reason: 'promo_not_found', stage: 'resolve' })
                this.statusText = 'Акция не найдена'
                this.$store.dispatch('callNotify', 'Акция не найдена')
                this.$router.replace('/projects').catch(() => undefined)
                return
            }

            const project = this.findStoreProject(projectId)
            const fromToggle =
                pickPromoRedirectByToggle(this.previewPayload, project) ||
                pickPromoRedirectByToggle(data.payload, project)
            const rawPath = fromToggle || data.redirectPath
            const normalizedPath = this.normalizeRedirectPath(rawPath)
            if (!normalizedPath) {
                this.logPromo('run_failed', {
                    reason: 'invalid_redirect_path',
                    redirectPath: rawPath,
                    usedToggleLinks: Boolean(fromToggle),
                })
                this.$store.dispatch('callNotify', 'Некорректный адрес перехода по промо')
                this.$router.replace('/projects').catch(() => undefined)
                return
            }

            let destination = withProjectPrefix(projectId, normalizedPath)
            if (claimableSmsKeysFromResolve(data, this.promoKey).length) {
                destination = withOpenSmsPromoQuery(destination)
            }
            this.goToRedirect(destination)
        },
        async run() {
            if (this.handled || this.runInFlight) {
                this.logPromo('run_skipped', {
                    reason: this.handled ? 'already_handled' : 'in_flight',
                })
                return
            }
            this.runInFlight = true
            try {
                await this.runUnsafe()
            } finally {
                this.runInFlight = false
            }
        },
        async runUnsafe() {
            const key = this.promoKey
            this.logPromo('run_started', {
                routeKey: this.$route.params.key || null,
                selectedKey: key || null,
            })
            if (!key) {
                this.logPromo('run_failed', { reason: 'missing_key' })
                this.statusText = 'Промо не найдено'
                this.$store.dispatch('callNotify', 'Некорректная промо-ссылка')
                this.$router.replace('/landing').catch(() => undefined)
                return
            }

            this.statusText = 'Проверяем авторизацию…'

            const isAuthenticated = await this.waitAuthReady()
            if (!isAuthenticated) {
                this.waitingForAuth = true
                this.logPromo('authorization_required')
                this.statusText = 'Для активации промо нужна авторизация'
                this.openAuthDialog()
                return
            }
            this.waitingForAuth = false

            this.statusText = 'Загружаем промо…'
            this.logPromo('preview_started')
            const { data, error } = await getPromoByKey(key)
            if (error || !data) {
                this.handled = true
                this.logPromo('preview_failed', {
                    error: typeof error === 'string' ? error : String(error ?? 'empty_response'),
                    hasData: Boolean(data),
                })
                this.statusText = 'Не удалось проверить промо'
                this.$store.dispatch(
                    'callNotify',
                    typeof error === 'string' ? error : 'Не удалось проверить промо'
                )
                this.$router.replace('/projects').catch(() => undefined)
                return
            }

            this.handled = true
            this.previewPayload = data.payload || null
            this.logPromo('preview_completed', {
                status: data.status,
                needProjectSelector: resolveNeedsProjectSelector(data),
                hasToggleLinks: Boolean(
                    this.previewPayload?.toggleKey && Array.isArray(this.previewPayload?.links)
                ),
            })

            if (data.status === 'not_found') {
                this.logPromo('run_failed', { reason: 'promo_not_found' })
                this.statusText = 'Акция не найдена'
                this.$store.dispatch('callNotify', 'Акция не найдена')
                this.$router.replace('/projects').catch(() => undefined)
                return
            }

            if (!resolveNeedsProjectSelector(data)) {
                this.logPromo('run_failed', { reason: 'selector_not_required' })
                this.$router.replace('/projects').catch(() => undefined)
                return
            }

            await this.openProjectSelector()
        },
    },
})
